import React, { useEffect, useState } from 'react';
import { Form, Grid } from 'antd';
import { LocalStorageService, MessageService } from 'services';
import ExpirePassword from 'containers/auth/ExpirePassword';
import { useLazyGetUserConfigsQuery } from 'app/userPreferences/api';
import { authApi } from 'app/auth/api';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { selectAuthenticatedStatus, selectAuthIsPasswordExpired, selectAuthIsTFAEnabled } from 'app/auth/selectors';
import { changeTFAState } from 'app/auth/slices';
import { useLazyGetResourceSystemSettingsQuery } from 'app/resource/api/systemSettings';
import { StyledAuthMainWrapper } from 'app/auth/components/Login.styled';
import { LoginData } from 'app/auth/types';
import {
  useLazyGetResourceCurrenciesPrecisionsQuery,
} from 'app/resource/api/currenciesPrecisions';
import LoginStep1 from './LoginStep1';
import LoginStep2 from './LoginStep2';

const { useLoginMutation, useLazyGetAuthUserQuery } = authApi;

export type HandleSubmitType = (e?: React.FormEvent, additionalData?: Record<string, string>) => void;

const Login = () => {
  const dispatch = useAppDispatch();

  const isAuthenticated = useAppSelector(selectAuthenticatedStatus);
  const isTFAEnabled = useAppSelector(selectAuthIsTFAEnabled);
  const isPasswordExpired = useAppSelector(selectAuthIsPasswordExpired);

  const { useBreakpoint } = Grid;

  const [login] = useLoginMutation();
  const [getAuthUser] = useLazyGetAuthUserQuery();

  const screens = useBreakpoint();

  const [getUserConfigs] = useLazyGetUserConfigsQuery();
  const [getCurrenciesPrecisions] = useLazyGetResourceCurrenciesPrecisionsQuery();
  const [getSystemSettings] = useLazyGetResourceSystemSettingsQuery();

  const [form] = Form.useForm();
  const [data, setData] = useState<Partial<LoginData>>({});

  const handleSubmit: HandleSubmitType = (e, additionalData = {}) => {
    if (e) {
      e.preventDefault();
    }
    form.validateFields().then((values) => {
      const { userName, password } = { ...data, ...values };
      if (userName?.trim() === password) {
        return MessageService.error('Your password cannot be the same as your username. Please choose a different password');
      }
      if (!isTFAEnabled) {
        setData({ ...values, ...additionalData });
      }

      login({
        ...data, ...values, ...additionalData, skipErrorHandler: true,
      });
    });
  };

  useEffect(
    () => () => {
      if (isTFAEnabled) {
        dispatch(changeTFAState(false));
      }
    },
    [dispatch, isTFAEnabled],
  );

  useEffect(() => {
    if (isAuthenticated && LocalStorageService.get('auth')) {
      getAuthUser();
      getUserConfigs(); // Pass empty object as required
      getCurrenciesPrecisions({
        skipGlobalLoading: true,
      });
      getSystemSettings({
        skipGlobalLoading: true,
      });
    }
  }, [
    isAuthenticated,
    getAuthUser,
    getUserConfigs, isTFAEnabled, getCurrenciesPrecisions,
    getSystemSettings,
  ]);

  return (
    <StyledAuthMainWrapper md={screens.md ? 1 : 0} xs={screens.xs ? 1 : 0}>
      {!isPasswordExpired && (
        <Form
          layout="vertical"
          initialValues={{
            userName: data?.userName,
            password: data?.password,
          }}
          form={form}
        >
          {!isTFAEnabled ? <LoginStep1 handleSubmit={handleSubmit} /> : <LoginStep2 handleSubmit={handleSubmit} changeTFAState={changeTFAState} />}
        </Form>
      )}
      {isPasswordExpired && (
        <ExpirePassword
          handleSubmit={handleSubmit}
          data={data}
          setData={setData}
          loginForm={form}
        />
      )}
    </StyledAuthMainWrapper>
  );
};

export default Login;
